import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box,
  CircularProgress,
  Typography,
  Alert,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Card,
  CardContent,
  IconButton,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  Slider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Skeleton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import { getAllAgents, getUserCalls } from '../services/voiceAnalytics';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Importerer ikoner
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MicIcon from '@mui/icons-material/Mic';
import SpeedIcon from '@mui/icons-material/Speed';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FilterListIcon from '@mui/icons-material/FilterList';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import InsightsIcon from '@mui/icons-material/Insights';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GradeIcon from '@mui/icons-material/Grade';
// Importerer radar chart - bemærk vi omdøber Tooltip til ChartTooltip
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Radar, Line } from 'react-chartjs-2';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';

// Registrer Chart.js komponenter
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  ChartTooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

interface Agent {
  id: string;
  name: string;
  email: string;
  callCount: number;
  avgSentiment: number;
  avgDuration: number;
  flagged?: boolean;
  department: string;
  callsHandled: number;
  avgHandleTime: number;
  avgCallScore: number;
  dominance: number;
  pitchVariation: number;
  speechRate: number;
  tlr: number;
  actionPunkt1?: string;
  actionPunkt2?: string;
  actionPunkt3?: string;
  actionPunkt4?: string;
}

// Forbedringspunkt interface
interface ImprovementPoint {
  id: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
}

// Dummy data til forbedringspunkter
const dummyImprovementPoints: { [key: string]: ImprovementPoint[] } = {
  'default': [
    { id: 'ip1', description: 'Forbedre stemmens klarhed', priority: 'high' },
    { id: 'ip2', description: 'Mere varieret tonehøjde', priority: 'medium' },
    { id: 'ip3', description: 'Optimér talehastighed', priority: 'low' },
    { id: 'ip4', description: 'Øget autoritet i stemmen', priority: 'medium' },
  ]
};

// Radar data for kompetence visualisering
const generateRadarData = (agent: Agent, t: any) => {
  // Log relevante værdier - NØJAGTIG som i AgentDashboard.tsx
  console.log("RADAR DATA INPUT:", agent.name, {
    dominance: agent.dominance,
    pitchVariation: agent.pitchVariation, 
    speechRate: agent.speechRate,
    rawValues: {
      dominance: typeof agent.dominance, 
      pitchVar: typeof agent.pitchVariation, 
      speechRate: typeof agent.speechRate
    }
  });
  
  // NØJAGTIG samme beregning som i AgentDashboard.tsx - linje 1122
  const data = [
    // dominanceMean - absolutværdi
    agent.dominance !== null && agent.dominance !== undefined ? 
      Math.abs(Number(agent.dominance)) / 1.0 : 0.2,
    
    // pitchStd
    agent.pitchVariation !== null && agent.pitchVariation !== undefined ? 
      Number(agent.pitchVariation) / 80.0 : 0.3,
    
    // speechRateMean
    agent.speechRate !== null && agent.speechRate !== undefined ? 
      Number(agent.speechRate) / 0.8 : 0.25
  ];
  
  console.log("RADAR OUTPUT - Beregnede værdier:", data);
  
  return {
    labels: [t('dashboard.authority'), t('dashboard.pitchVariation'), t('dashboard.speechRate')],
    datasets: [
      {
        label: 'Kompetencer',
        data: data,
        backgroundColor: 'rgba(63, 81, 181, 0.15)',
        borderColor: 'rgba(63, 81, 181, 0.8)',
        borderWidth: 2.5,
        pointBackgroundColor: '#3f51b5',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: '#3f51b5',
        pointBorderWidth: 2,
      }
    ]
  };
};

// Styled components
const AppContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  backgroundColor: theme.palette.background.default,
}));

const Sidebar = styled(Box)(({ theme }) => ({
  width: 100,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2, 0),
  boxShadow: theme.shadows[2],
  zIndex: 10,
}));

const MainContent = styled(Box)({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[1],
}));

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 5),
  overflow: 'auto',
  flexGrow: 1,
  backgroundColor: '#f8fafc',
  backgroundImage: 'radial-gradient(at 50% 0%, rgba(25, 118, 210, 0.05) 0%, transparent 75%)',
}));

const MenuItemContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  width: '100%',
  cursor: 'pointer',
  transition: 'color 0.2s, background-color 0.2s',
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MenuItemText = styled(Typography)({
  fontSize: '0.75rem',
  marginTop: 4,
});

const StyledTable = styled(TableContainer)(({ theme }) => ({
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: '12px',
  overflow: 'hidden',
  '& .MuiTableCell-head': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.primary.dark,
    fontWeight: 600,
  }
}));

const AgentRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  }
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(2, 2.5),
  height: '100%',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
  }
}));

const AgentDetailCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
  marginBottom: theme.spacing(3)
}));

const ImprovementPointChip = styled(Chip)<{ priority: string }>(({ theme, priority }) => {
  const getColor = () => {
    switch (priority) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      default: return theme.palette.info.main;
    }
  };
  
  return {
    backgroundColor: alpha(getColor(), 0.1),
    color: getColor(),
    fontWeight: 500,
    margin: theme.spacing(0.5),
    '& .MuiChip-label': {
      padding: '0 8px',
    }
  };
});

const alpha = (color: string, opacity: number): string => {
  // Simpel alpha-funktion hvis styled-components har problemer med MUI's alpha
  return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
};

const ActionButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  marginLeft: theme.spacing(1),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  }
}));

// Tilføj FilterContainer styled component
const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: '12px'
}));

// Race Track Styled Components
const RaceTrack = styled(Paper)(({ theme }) => ({
  position: 'relative',
  height: '250px',
  background: 'linear-gradient(to right, #1a237e, #283593, #3949ab, #3f51b5, #5c6bc0, #7986cb)',
  borderRadius: '16px',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.1)',
}));

const RaceTrackOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
  backgroundSize: '20px 20px',
  pointerEvents: 'none',
});

const RaceTrackGlow = styled(Box)({
  position: 'absolute',
  top: 0,
  right: '10px',
  height: '100%',
  width: '100px',
  background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 100%)',
  pointerEvents: 'none',
});

const RaceTrackLine = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: 0,
  right: 0,
  height: '3px',
  background: 'linear-gradient(90deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.7) 100%)',
  transform: 'translateY(-50%)',
  boxShadow: '0 0 10px rgba(255,255,255,0.5)',
});

const TrackMarker = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  width: '3px',
  height: '16px',
  background: 'rgba(255,255,255,0.8)',
  transform: 'translateY(-50%)',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '-15px',
    left: '-17px',
    width: '40px',
    height: '40px',
    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
    borderRadius: '50%',
  }
}));

const MarkerLabel = styled(Typography)({
  position: 'absolute',
  top: '65%',
  transform: 'translateX(-50%)',
  color: 'rgba(255,255,255,0.8)',
  fontSize: '0.75rem',
  fontWeight: 'bold',
});

const FinishLine = styled(Box)({
  position: 'absolute',
  top: 0,
  right: '30px',
  height: '100%',
  width: '6px',
  background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9) 10px, rgba(0,0,0,0.6) 10px, rgba(0,0,0,0.6) 20px)',
  boxShadow: '0 0 15px rgba(255,255,255,0.7)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-15px',
    height: '100%',
    width: '15px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 100%)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: '-15px',
    height: '100%',
    width: '15px',
    background: 'linear-gradient(90deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)',
  }
});

const FinishFlag = styled(Box)({
  position: 'absolute',
  top: '10px',
  right: '36px',
  color: 'white',
  fontSize: '26px',
  textShadow: '0 0 8px rgba(255,255,255,0.7)',
});

const GoalLabel = styled(Typography)({
  position: 'absolute',
  top: '10px',
  right: '36px',
  transform: 'translateX(120%)',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '1.1rem',
  textShadow: '0 2px 5px rgba(0,0,0,0.5)',
});

const RunnerAvatar = styled(Avatar)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: 60,
  height: 60,
  border: '3px solid #fff',
  transition: 'left 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
  zIndex: 2,
  boxShadow: '0 0 15px rgba(0,0,0,0.4)',
  '&:hover': {
    transform: 'translate(-50%, -50%) scale(1.1)',
    boxShadow: '0 0 20px rgba(255,255,255,0.6)',
    zIndex: 10,
  }
}));

const RunnerName = styled(Typography)({
  position: 'absolute',
  top: '-45px',
  transform: 'translateX(-50%)',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  color: 'white',
  textShadow: '0 1px 3px rgba(0,0,0,0.7)',
  whiteSpace: 'nowrap',
  backgroundColor: 'rgba(0,0,0,0.5)',
  padding: '2px 8px',
  borderRadius: '10px',
  zIndex: 3,
});

const ScoreBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '10px',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.primary.dark,
  color: 'white',
  padding: '3px 8px',
  borderRadius: '12px',
  fontSize: '0.85rem',
  fontWeight: 'bold',
  boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.3)',
}));

const AvatarGlow = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%)',
  filter: 'blur(5px)',
  zIndex: 1,
  transition: 'left 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
}));

// Race interface og dummy data
interface RaceUser {
  id: string;
  name: string;
  score: number;
  improvement: number;
  avatarUrl?: string;
  rank?: number;
}

// Nye premium layout styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  color: '#334155',
  marginBottom: theme.spacing(4),
  position: 'relative',
  paddingBottom: theme.spacing(1),
  '&:after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: 60,
    height: 3,
    background: theme.palette.primary.main,
    borderRadius: 3,
  },
}));

const SectionWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(5),
  position: 'relative',
  padding: theme.spacing(1, 0),
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-40px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '85%',
    height: '1px',
    background: 'linear-gradient(90deg, rgba(0,0,0,0), rgba(0,0,0,0.03), rgba(0,0,0,0))',
    zIndex: 0,
  }
}));

const StatsCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  background: 'white',
  height: '100%',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
}));

const StatsCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3, 3),
  position: 'relative',
  zIndex: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
}));

const StatsLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.9rem',
  fontWeight: 500,
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
}));

const StatsValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '2.5rem',
  fontWeight: 600,
  letterSpacing: '-0.5px',
  marginBottom: theme.spacing(0.5),
}));

const ChartCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  background: 'white',
  height: '100%',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  },
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: 'linear-gradient(90deg, #3B82F6, #60A5FA)',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  animation: 'fadeIn 0.6s ease-out forwards'
}));

const ChartCardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(249, 250, 251, 0.5)',
}));

const ChartCardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
}));

const ChartCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  height: '400px',
}));

const LeaderboardCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
  height: '100%',
  overflow: 'hidden',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  },
}));

const LeaderboardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
}));

const LeaderboardTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
}));

// Tilføj en ny styled komponent til sektionsadskilleren
const SectionDivider = styled(Box)(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(6, 0),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1, 0),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.6), rgba(59, 130, 246, 0.1))',
    zIndex: 0,
  }
}));

const DividerIcon = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'white',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(59, 130, 246, 0.2)',
  zIndex: 1,
  position: 'relative',
  marginRight: theme.spacing(2),
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1) rotate(10deg)',
  }
}));

const DividerContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: 'white',
  padding: theme.spacing(0, 3),
  borderRadius: '30px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  zIndex: 1,
  position: 'relative',
  border: '1px solid rgba(59, 130, 246, 0.2)',
  cursor: 'pointer',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }
}));

const DividerText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
}));

const ManagementDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState("Oversigt");
  const navigate = useNavigate();
  
  // Ny state til filtrering
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [voiceScoreRange, setVoiceScoreRange] = useState<[number, number]>([0, 100]);
  
  // Ny state til race data
  const [raceUsers, setRaceUsers] = useState<RaceUser[]>([
    { id: '1', name: 'Anders Jensen', score: 78, improvement: 12 },
    { id: '2', name: 'Marie Nielsen', score: 92, improvement: 7 },
    { id: '3', name: 'Søren Hansen', score: 65, improvement: 15 },
    { id: '4', name: 'Mette Olsen', score: 84, improvement: 8 },
    { id: '5', name: 'Lars Pedersen', score: 45, improvement: 20 }
  ]);
  
  // State til voice score historik
  const [voiceScoreHistory, setVoiceScoreHistory] = useState<number[]>([]);
  
  // State til gennemsnitlig voice score over tid
  const [avgVoiceScoreHistory, setAvgVoiceScoreHistory] = useState<number[]>([]);
  
  // Filtrer agents baseret på valgte filtre
  const filteredAgents = agents.filter(agent => {
    // Filter efter voice score range
    if (agent.avgSentiment < voiceScoreRange[0] || agent.avgSentiment > voiceScoreRange[1]) {
      return false;
    }
    
    // Filter efter flag status
    if (onlyFlagged && !agent.flagged) {
      return false;
    }
    
    return true;
  });
  
  // Toggle flag på en agent
  const handleToggleFlag = (agentId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Forhindrer at row click bliver aktiveret
    setAgents(prevAgents => 
      prevAgents.map(agent => 
        agent.id === agentId ? { ...agent, flagged: !agent.flagged } : agent
      )
    );
  };
  
  // Håndtér voice score range ændring
  const handleVoiceScoreChange = (event: Event, newValue: number | number[]) => {
    setVoiceScoreRange(newValue as [number, number]);
  };

  // Toggle filterpanel
  const toggleFilterPanel = () => {
    setShowFilterPanel(!showFilterPanel);
  };
  
  // Ryd filtre
  const clearFilters = () => {
    setOnlyFlagged(false);
    setVoiceScoreRange([0, 100]);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        try {
          // Hent agent-data fra Firebase med currentUser.uid for at bekræfte manager-rettigheder
          const agentsData = await getAllAgents(currentUser.uid);
          
          if (agentsData.length === 0) {
            setAgents([]);
            setLoading(false);
            return;
          }
          
          // Her indsamler vi call-data for hver agent for at beregne deres statistikker
          const agentsWithStats = await Promise.all(
            agentsData.map(async (agent: any) => {
              try {
                // Hent opkald og beregn statistikker for denne agent
                const agentCalls = await getUserCalls(agent.id);
                
                console.log(`Hentede ${agentCalls.length} opkald for agent ${agent.name} (id: ${agent.id}, user_id: ${agent.user_id})`);
                
                // Find den seneste voice score for denne agent fra rule_result_agent
                let latestVoiceScore = 0;
                let latestVoiceScoreRaw = 0;
                let latestDominance = 0;
                let latestSpeechRate = 0;
                let latestPitchVariation = 0;
                let latestTlr = 0;
                // Tilføj variabler for action punkter
                let actionPunkt1 = '';
                let actionPunkt2 = '';
                let actionPunkt3 = '';
                let actionPunkt4 = '';

                if (agentCalls && agentCalls.length > 0) {
                  // Vi skal hente rule_result_agent data direkte, da getUserCalls ikke returnerer voice_score
                  const ruleResultRef = collection(db, 'rule_result_agent');
                  const q = query(ruleResultRef, where('user_id', '==', agent.user_id));
                  const querySnapshot = await getDocs(q);
                  
                  console.log(`Hentede ${querySnapshot.docs.length} rule_result_agent dokumenter for ${agent.name} (id: ${agent.id}, user_id: ${agent.user_id})`);
                  
                  // Konverter alle dokumenter til data med timestamp
                  const allDocs = querySnapshot.docs.map(doc => {
                    const data = doc.data();
                    return {
                      ...data,
                      id: doc.id,
                      timestamp: data.timestamp
                    } as any; // Tilføjer 'as any' for at undgå TypeScript fejl
                  });
                  
                  // Sortér dokumenterne efter timestamp (nyeste først)
                  const sortedDocs = [...allDocs].sort((a, b) => {
                    const getTime = (timestamp: any) => {
                      if (timestamp && typeof timestamp.toDate === 'function') {
                        return timestamp.toDate().getTime();
                      } else if (timestamp && typeof timestamp === 'string') {
                        return new Date(timestamp).getTime();
                      }
                      return 0;
                    };
                    
                    return getTime(b.timestamp) - getTime(a.timestamp);
                  });
                  
                  // Find det første dokument med observed_values
                  for (const doc of sortedDocs) {
                    // Log for debugging
                    console.log(`Tjekker dokument for ${agent.name} med timestamp:`, 
                      doc.timestamp?.toDate ? doc.timestamp.toDate().toLocaleString() : 'ukendt tidspunkt');
                    
                    // Tjek først om voice_score findes
                    if (doc.voice_score) {
                      latestVoiceScoreRaw = parseFloat(doc.voice_score);
                      latestVoiceScore = Math.round(latestVoiceScoreRaw * 10) / 10;
                      console.log(`Fandt voice_score for ${agent.name}: ${latestVoiceScoreRaw}`);
                    }
                    
                    // Hent action punkter hvis de findes
                    if (doc.action_punkt_1) {
                      actionPunkt1 = doc.action_punkt_1;
                    }
                    
                    if (doc.action_punkt_2) {
                      actionPunkt2 = doc.action_punkt_2;
                    }
                    
                    // Tjek derefter om observed_values findes
                    if (doc.observed_values) {
                      console.log(`FANDT observed_values for ${agent.name}:`, doc.observed_values);
                      
                      // Meget vigtig del - eksplicit konvertering med Number() og ingen defaults
                      latestDominance = Number(doc.observed_values.dominance_mean);
                      latestSpeechRate = Number(doc.observed_values.speech_rate_mean);
                      latestPitchVariation = Number(doc.observed_values.pitch_std);
                      
                      // Hent TLR-værdi hvis den findes
                      if (doc.observed_values.tlr !== undefined) {
                        latestTlr = Number(doc.observed_values.tlr);
                      }
                      
                      console.log(`KRITISK VÆRDI - Kompetencedata for ${agent.name}:`, {
                        dominance: latestDominance,
                        speechRate: latestSpeechRate,
                        pitchVariation: latestPitchVariation,
                        tlr: latestTlr,
                        rawData: {
                          dominance_mean: doc.observed_values.dominance_mean,
                          speech_rate_mean: doc.observed_values.speech_rate_mean,
                          pitch_std: doc.observed_values.pitch_std,
                          tlr: doc.observed_values.tlr
                        }
                      });
                      
                      // Break kun hvis vi har fundet observed_values
                      break;
                    } else {
                      console.log(`Ingen observed_values fundet i dette dokument for ${agent.name}`);
                    }
                  }
                  
                  if (latestDominance === 0 && latestPitchVariation === 0 && latestSpeechRate === 0) {
                    console.error(`ADVARSEL: Ingen observed_values fundet for ${agent.name} i nogen dokumenter!`);
                    
                    // Log alle tilgængelige felter for det første dokument (hvis det findes)
                    if (sortedDocs.length > 0) {
                      console.log("Tilgængelige felter i første dokument:", Object.keys(sortedDocs[0]));
                      console.log("Første dokuments indhold:", sortedDocs[0]);
                    }
                  }
                }
                
                // Beregn statistikker baseret på faktiske opkald
                const avgSentiment = latestVoiceScore || 0;
                
                const avgDuration = agentCalls.length > 0 
                  ? agentCalls.reduce((sum, call) => sum + call.duration, 0) / agentCalls.length 
                  : 0;
                
                return {
                  id: agent.id,
                  name: agent.name || 'Unavngivet agent',
                  email: agent.email || '',
                  callCount: agentCalls.length,
                  avgSentiment: avgSentiment,
                  avgDuration: Math.floor(avgDuration),
                  flagged: false,
                  department: agent.department || 'Ukendt',
                  callsHandled: agentCalls.length,
                  avgHandleTime: Math.floor(avgDuration),
                  avgCallScore: latestVoiceScoreRaw || 0, // Rå score 0-100
                  dominance: latestDominance,
                  pitchVariation: latestPitchVariation,
                  speechRate: latestSpeechRate,
                  tlr: latestTlr,
                  actionPunkt1: actionPunkt1,
                  actionPunkt2: actionPunkt2
                };
              } catch (error) {
                console.error(`Fejl ved hentning af data for agent ${agent.id}:`, error);
                // Returnér agent med tomme værdier i tilfælde af fejl
                return {
                  id: agent.id,
                  name: agent.name || 'Unavngivet agent',
                  email: agent.email || '',
                  callCount: 0,
                  avgSentiment: 0,
                  avgDuration: 0,
                  flagged: false,
                  department: 'Ukendt',
                  callsHandled: 0,
                  avgHandleTime: 0,
                  avgCallScore: 0,
                  dominance: 0,
                  pitchVariation: 0,
                  speechRate: 0,
                  tlr: 0,
                  actionPunkt1: '',
                  actionPunkt2: ''
                };
              }
            })
          );
          
          console.log('Agenter med statistik:', agentsWithStats);
          setAgents(agentsWithStats);
          
          // Forbered race data baseret på agent data
          const raceData: RaceUser[] = agentsWithStats.map((agent: Agent, index: number) => ({
            id: agent.id,
            name: agent.name,
            score: Math.round(agent.avgSentiment), // Bruger raw score direkte, allerede 0-100
            improvement: Math.floor(Math.random() * 20), // Dummy data til forbedring
          }));
          
          // Sortér efter score og opdat rangering
          const sortedRacers = [...raceData].sort((a, b) => b.score - a.score);
          sortedRacers.forEach((racer, index) => {
            racer.rank = index + 1;
          });
          
          setRaceUsers(sortedRacers);
          
          // Opret dummy data til voice score historik
          const lastMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun'];
          const dummyScores = [3.2, 3.4, 3.5, 3.7, 3.8, 4.1];
          
          setVoiceScoreHistory(dummyScores);
          
          // Opret data til gennemsnitlige voice scores over tid
          setAvgVoiceScoreHistory(dummyScores);
        } catch (agentError) {
          console.error("Fejl ved hentning af agent data:", agentError);
          setError("Kunne ikke hente agent-data. Dette kan skyldes manglende rettigheder eller netværksproblemer.");
          setAgents([]);
        }
      } catch (error) {
        console.error('Fejl ved hentning af ledelsesdata:', error);
        setError("Der opstod en fejl ved indlæsning af management dashboard. Prøv igen senere.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleNavigate = (section: string) => {
    setActiveSection(section);
    // Nulstil valgt agent ved skift mellem sektioner
    setSelectedAgent(null);
  };

  const handleSelectAgent = (agent: Agent) => {
    setSelectedAgent(agent);
  };

  const handleBackToList = () => {
    setSelectedAgent(null);
  };

  // Opdater getImprovementPoints funktionen til at kun vise de faktiske action punkter
  const getImprovementPoints = (agentId: string): ImprovementPoint[] => {
    const agent = agents.find(a => a.id === agentId);
    
    if (!agent) {
      return [];
    }
    
    const result: ImprovementPoint[] = [];
    
    // Tilføj actionPunkt1 hvis den findes
    if (agent.actionPunkt1 && agent.actionPunkt1.trim() !== '') {
      result.push({
        id: 'real-1',
        description: agent.actionPunkt1,
        priority: 'high'
      });
    }
    
    // Tilføj actionPunkt2 hvis den findes
    if (agent.actionPunkt2 && agent.actionPunkt2.trim() !== '') {
      result.push({
        id: 'real-2',
        description: agent.actionPunkt2,
        priority: 'medium'
      });
    }
    
    // Hvis vi ikke fandt nogen action punkter, returner tom array i stedet for dummy data
    return result;
  };

  // Funktion til at returnere udviklingstrenden (positiv/negativ)
  const getDevelopmentTrend = (agent: Agent): 'positive' | 'negative' | 'neutral' => {
    // I et rigtigt system ville dette være baseret på faktisk data over tid
    return agent.avgDuration > 50 ? 'positive' : agent.avgDuration > 30 ? 'neutral' : 'negative';
  };

  // Leaderboard rendering
  const renderLeaderboard = () => (
    <Card sx={{ borderRadius: '12px', height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Top Performere
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {raceUsers
            .sort((a, b) => b.score - a.score)
            .slice(0, 3)
            .map((user, index) => (
              <ListItem key={user.id} sx={{ px: 1 }}>
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: index === 0 ? 'gold' : index === 1 ? 'silver' : '#cd7f32',
                    color: index === 0 ? '#000' : '#fff'
                  }}>
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={user.name} 
                  secondary={`Score: ${user.score}`} 
                />
                <Chip 
                  label={`${user.score} point`} 
                  color="primary" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </ListItem>
            ))}
        </List>
      </CardContent>
    </Card>
  );
  
  // Improvement leaderboard rendering
  const renderImprovementLeaderboard = () => (
    <Card sx={{ borderRadius: '12px', height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Mest Forbedrede
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <List>
          {raceUsers
            .sort((a, b) => b.improvement - a.improvement)
            .slice(0, 3)
            .map((user, index) => (
              <ListItem key={user.id} sx={{ px: 1 }}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    {index + 1}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText 
                  primary={user.name} 
                  secondary={`Forbedring: +${user.improvement}%`} 
                />
                <Chip 
                  icon={<ArrowUpwardIcon />}
                  label={`+${user.improvement}%`} 
                  color="secondary" 
                  size="small" 
                  sx={{ ml: 1 }}
                />
              </ListItem>
            ))}
        </List>
      </CardContent>
    </Card>
  );

  // Beregn statistikker
  const getTotalCalls = () => {
    return agents.reduce((total, agent) => total + agent.callsHandled, 0);
  };
  
  const getAvgHandleTime = () => {
    const total = agents.reduce((total, agent) => total + (agent.avgHandleTime * agent.callsHandled), 0);
    return Math.round(total / getTotalCalls());
  };
  
  const getAvgCallScore = () => {
    const total = agents.reduce((total, agent) => total + (agent.avgCallScore * agent.callsHandled), 0);
    return Math.round(total / getTotalCalls());
  };

  // Tilføj en ny funktion til at beregne gennemsnitlig TLR
  const getAvgTLR = () => {
    // Filtrerer ud for agenter uden TLR værdi
    const agentsWithTLR = agents.filter(agent => agent.tlr !== undefined && agent.tlr !== 0);
    
    if (agentsWithTLR.length === 0) return 0;
    
    const total = agentsWithTLR.reduce((sum, agent) => sum + agent.tlr, 0);
    return (total / agentsWithTLR.length).toFixed(2);
  };

  // Tilføj/modificer funktion til at beregne gennemsnitlig voice score
  const getAvgVoiceScore = () => {
    if (agents.length === 0) return 0;
    
    const total = agents.reduce((sum, agent) => sum + agent.avgCallScore, 0);
    return Math.round(total / agents.length);
  };

  // Race track rendering
  const renderRaceTrack = () => {
    const maxScore = 100;
    const trackWidth = 95; // 95% of the track width, leaving space for finish line
    
    // Sortér brugerne efter score højest først
    const sortedUsers = [...raceUsers].sort((a, b) => b.score - a.score);
    
    return (
      <RaceTrack>
        <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 'bold', textShadow: '0 2px 5px rgba(0,0,0,0.5)', mb: 3 }}>
          <InsightsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Voice Score Race
        </Typography>
        
        <RaceTrackOverlay />
        <RaceTrackGlow />
        <RaceTrackLine />
        
        {/* Milepæls markører */}
        {[25, 50, 75].map(marker => (
          <React.Fragment key={marker}>
            <TrackMarker sx={{ left: `${marker}%` }} />
            <MarkerLabel sx={{ left: `${marker}%` }}>{marker}</MarkerLabel>
          </React.Fragment>
        ))}
        
        {/* Mållinje */}
        <FinishLine />
        <FinishFlag>🏁</FinishFlag>
        <GoalLabel>100</GoalLabel>
        
        {/* Løbere (avatarer med score) */}
        {sortedUsers.map((user, index) => {
          const position = Math.min(user.score, 95);
          return (
            <Box key={user.id} sx={{ position: 'relative' }}>
              <AvatarGlow sx={{ left: `${position}%` }} />
              <RunnerAvatar 
                sx={{ 
                  left: `${position}%`,
                  bgcolor: index === 0 ? 'gold' : 
                           index === 1 ? '#3f51b5' : 
                           index === 2 ? '#009688' :
                           index === 3 ? '#ff5722' : '#607d8b'
                }}
              >
                {user.name.charAt(0).toUpperCase()}
              </RunnerAvatar>
              <ScoreBox sx={{ left: `${position}%` }}>
                {user.score}
              </ScoreBox>
              <RunnerName sx={{ left: `${position}%` }}>
                {user.name}
              </RunnerName>
            </Box>
          );
        })}
      </RaceTrack>
    );
  };
  
  // Voice score chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'end' as const,
      },
      tooltip: {
        backgroundColor: 'white',
        titleColor: '#334155',
        bodyColor: '#334155',
        padding: 15,
        cornerRadius: 10,
        borderColor: 'rgba(0,0,0,0.1)',
        borderWidth: 1,
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(0, 0, 0, 0.03)',
        },
        ticks: {
          color: '#64748b',
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#64748b',
        }
      },
    },
    elements: {
      line: {
        tension: 0.4,
        borderWidth: 3,
      },
      point: {
        radius: 4,
        hitRadius: 10,
        hoverRadius: 6,
      }
    }
  };
  
  // Voice score chart data
  const chartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov'],
    datasets: [
      {
        label: 'Top Agent',
        data: voiceScoreHistory,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.2)',
        tension: 0.4,
        fill: true,
      },
      {
        label: 'Gennemsnit',
        data: avgVoiceScoreHistory,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };
  
  // Rendering af voice score chart
  const renderVoiceScoreChart = () => (
    <Card sx={{ borderRadius: '12px', height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          <InsightsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Voice Score Over Tid
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ height: 300 }}>
          <Line options={chartOptions} data={chartData} />
        </Box>
      </CardContent>
    </Card>
  );
  
  // Rendering af hovedindhold baseret på aktiv sektion
  const renderContent = () => {
    switch (activeSection) {
      case "Oversigt":
        return (
          <>
            <SectionTitle>
              Team Overblik
            </SectionTitle>
            
            <SectionWrapper>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <StatsCard>
                    <StatsCardContent>
                      <StatsLabel>
                        <MicIcon sx={{ fontSize: '1.1rem', color: 'primary.main', mr: 1 }} />
                        Gennemsnitlig Voice Score
                      </StatsLabel>
                      <StatsValue>
                        {loading ? <Skeleton width={80} /> : `${getAvgVoiceScore()}/100`}
                      </StatsValue>
                      <Typography variant="caption" color="text.secondary">
                        Voice score på tværs af alle agenter
                      </Typography>
                    </StatsCardContent>
                  </StatsCard>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <StatsCard>
                    <StatsCardContent>
                      <StatsLabel>
                        <RecordVoiceOverIcon sx={{ fontSize: '1.1rem', color: 'primary.main', mr: 1 }} />
                        Gennemsnitlig Talk Ratio
                      </StatsLabel>
                      <StatsValue>
                        {loading ? <Skeleton width={80} /> : getAvgTLR()}
                      </StatsValue>
                      <Typography variant="caption" color="text.secondary">
                        TR på tværs af alle agenter
                      </Typography>
                    </StatsCardContent>
                  </StatsCard>
                </Grid>
              </Grid>
            </SectionWrapper>
            
            <SectionWrapper>
              {renderRaceTrack()}
            </SectionWrapper>
            
            <SectionDivider>
              <DividerContent>
                <DividerIcon>
                  <InsightsIcon color="primary" fontSize="small" />
                </DividerIcon>
                <DividerText>Statistik og Analyse</DividerText>
              </DividerContent>
            </SectionDivider>
            
            <SectionWrapper>
              <Grid container spacing={4}>
                <Grid item xs={12} md={12}>
                  <ChartCard>
                    <ChartCardHeader>
                      <ChartCardTitle>
                        <InsightsIcon sx={{ fontSize: '1.2rem', color: 'primary.main', mr: 1 }} />
                        Voice Score Over Tid
                        <Box 
                          component="span" 
                          sx={{ 
                            ml: 2, 
                            fontSize: '0.75rem', 
                            color: 'text.secondary',
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            bgcolor: 'rgba(59, 130, 246, 0.08)',
                            border: '1px solid rgba(59, 130, 246, 0.15)'
                          }}
                        >
                          Seneste 6 måneder
                        </Box>
                      </ChartCardTitle>
                      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                        <Tooltip title="Opdateret i realtid">
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              color: 'success.main',
                              fontSize: '0.75rem',
                              fontWeight: 600
                            }}
                          >
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                bgcolor: 'success.main',
                                mr: 1,
                                boxShadow: '0 0 0 3px rgba(46, 204, 113, 0.15)'
                              }} 
                            />
                            Live
                          </Box>
                        </Tooltip>
                      </Box>
                    </ChartCardHeader>
                    <ChartCardContent>
                      <Line options={chartOptions} data={chartData} />
                    </ChartCardContent>
                  </ChartCard>
                </Grid>
              </Grid>
            </SectionWrapper>
          </>
        );
        
      case "Medarbejdere":
        return (
          <Box>
            {!selectedAgent ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h5">{t('dashboard.teamOverview')}</Typography>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={toggleFilterPanel}
                    startIcon={<FilterListIcon />}
                  >
                    {t('dashboard.filter')}
                  </Button>
                </Box>
                
                {/* Filter Panel */}
                {showFilterPanel && (
                  <FilterContainer>
                    <Typography variant="subtitle1" gutterBottom>{t('dashboard.filterTeam')}</Typography>
                    
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} md={6}>
                        <Typography gutterBottom>
                          {t('dashboard.voiceScoreRange')}: {voiceScoreRange[0].toFixed(1)} - {voiceScoreRange[1].toFixed(1)}
                        </Typography>
                        <Slider
                          value={voiceScoreRange}
                          onChange={handleVoiceScoreChange}
                          valueLabelDisplay="auto"
                          min={0}
                          max={100}
                          step={5}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox 
                              checked={onlyFlagged}
                              onChange={(e) => setOnlyFlagged(e.target.checked)}
                            />
                          }
                          label={t('dashboard.showFollowedAgents')}
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button size="small" onClick={clearFilters}>
                          {t('dashboard.resetFilters')}
                        </Button>
                      </Grid>
                    </Grid>
                    
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">
                        {t('dashboard.agentsShown', { count: filteredAgents.length })}
                      </Typography>
                    </Box>
                  </FilterContainer>
                )}
                
                <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <StyledTable>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('dashboard.agent')}</TableCell>
                          <TableCell align="center">{t('dashboard.voiceScore')}</TableCell>
                          <TableCell align="center">{t('dashboard.development')}</TableCell>
                          <TableCell>{t('dashboard.improvementPoints')}</TableCell>
                          <TableCell align="right">{t('dashboard.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAgents.length > 0 ? (
                          filteredAgents.map((agent) => {
                            const improvementPoints = getImprovementPoints(agent.id);
                            const trend = getDevelopmentTrend(agent);
                            
                            return (
                              <AgentRow 
                                key={agent.id} 
                                onClick={() => handleSelectAgent(agent)}
                                hover
                                sx={{
                                  bgcolor: agent.flagged ? alpha('#ffc107', 0.1) : 'inherit',
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar 
                                      sx={{ 
                                        bgcolor: agent.flagged ? 'warning.main' : 'primary.main',
                                        mr: 2
                                      }}
                                    >
                                      {agent.name.charAt(0)}
                                    </Avatar>
                                    <Box>
                                      <Typography variant="body1" fontWeight={500}>
                                        {agent.name}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {agent.email}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={`${agent.avgSentiment.toFixed(1)}`}
                                    size="small"
                                    sx={{ 
                                      bgcolor: alpha('#1976d2', 0.1),
                                      color: '#1976d2',
                                      fontWeight: 600,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {trend === 'positive' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <ArrowUpwardIcon color="success" fontSize="small" />
                                      <Typography variant="body2" color="success.main" sx={{ ml: 0.5 }}>
                                        {t('dashboard.positive')}
                                      </Typography>
                                    </Box>
                                  ) : trend === 'neutral' ? (
                                    <Typography variant="body2" color="text.secondary">
                                      {t('dashboard.stable')}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="error">
                                      {t('dashboard.declining')}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {improvementPoints.slice(0, 2).map((point) => (
                                      <ImprovementPointChip
                                        key={point.id}
                                        label={point.description}
                                        priority={point.priority}
                                        size="small"
                                      />
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    {/* Følg/Følger-knap med ikon og tekst under */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 1 }}>
                                      <IconButton 
                                        size="small"
                                        color={agent.flagged ? "warning" : "default"}
                                        onClick={(e) => handleToggleFlag(agent.id, e)}
                                      >
                                        {agent.flagged ? 
                                          <NotificationsActiveIcon fontSize="small" /> : 
                                          <NotificationsNoneIcon fontSize="small" />
                                        }
                                      </IconButton>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {agent.flagged ? t('dashboard.following') : t('dashboard.follow')}
                                      </Typography>
                                    </Box>
                                    
                                    {/* Vis profil-knap med ikon og tekst under */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mx: 1 }}>
                                      <IconButton 
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSelectAgent(agent);
                                        }}
                                      >
                                        <PersonIcon fontSize="small" />
                                      </IconButton>
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {t('dashboard.profile')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                              </AgentRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                              <Typography color="text.secondary">
                                {t('dashboard.noAgentsMatchFilters')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </StyledTable>
                </Paper>
              </>
            ) : (
              <Box>
                <Button 
                  startIcon={<ArrowBackIcon />} 
                  onClick={handleBackToList}
                  sx={{ mb: 3 }}
                >
                  {t('dashboard.backToOverview')}
                </Button>

                <AgentDetailCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar 
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: selectedAgent.flagged ? 'warning.main' : 'primary.main',
                        mr: 2
                      }}
                    >
                      {selectedAgent.name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5">{selectedAgent.name}</Typography>
                      <Typography variant="body1" color="text.secondary">{selectedAgent.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <IconButton 
                        onClick={(e) => handleToggleFlag(selectedAgent.id, e)}
                        color={selectedAgent.flagged ? "warning" : "default"}
                      >
                        {selectedAgent.flagged ? 
                          <NotificationsActiveIcon /> : 
                          <NotificationsNoneIcon />
                        }
                      </IconButton>
                      <Typography variant="caption" sx={{ mt: 0.5 }}>
                        {selectedAgent.flagged ? t('dashboard.following') : t('dashboard.follow')}
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                      <MetricCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <MicIcon sx={{ color: 'primary.main', mr: 1 }} />
                          <Typography variant="subtitle2">{t('dashboard.voiceScore')}</Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700} color="primary.main">
                          {selectedAgent.avgSentiment.toFixed(1)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.voiceScore')}
                        </Typography>
                      </MetricCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MetricCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <RecordVoiceOverIcon sx={{ color: 'info.main', mr: 1 }} />
                          <Typography variant="subtitle2">{t('dashboard.talkRatio')}</Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700} color="info.main">
                          {selectedAgent.tlr ? selectedAgent.tlr.toFixed(2) : t('dashboard.na')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.tr')}
                        </Typography>
                      </MetricCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <MetricCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <SpeedIcon sx={{ color: 'warning.main', mr: 1 }} />
                          <Typography variant="subtitle2">{t('dashboard.avgDuration')}</Typography>
                        </Box>
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                          {selectedAgent.avgDuration}s
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('dashboard.avgCallDuration')}
                        </Typography>
                      </MetricCard>
                    </Grid>
                  </Grid>

                  <Grid container spacing={4}>
                    <Grid item xs={12} md={7}>
                      <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboard.competenceProfile')}</Typography>
                      <Box sx={{ height: 350, p: 2 }}>
                        <Radar
                          data={generateRadarData(selectedAgent, t)}
                          options={{
                            scales: {
                              r: {
                                min: 0,
                                max: 1,
                                ticks: {
                                  stepSize: 0.2,
                                  backdropColor: 'rgba(0, 0, 0, 0)',
                                  display: false
                                },
                                pointLabels: {
                                  font: {
                                    family: "'Poppins', sans-serif",
                                    size: 16,
                                    weight: 600
                                  },
                                  color: '#334155',
                                  padding: 10
                                },
                                grid: {
                                  circular: true,
                                  color: (context) => {
                                    const index = context.index;
                                    const alpha = 0.03 + (index * 0.02);
                                    return `rgba(63, 81, 181, ${alpha})`;
                                  },
                                  lineWidth: (context) => {
                                    const index = context.index;
                                    return index === 0 ? 0 : index === 5 ? 2 : 1;
                                  }
                                },
                                angleLines: {
                                  lineWidth: 1.5,
                                  color: 'rgba(63, 81, 181, 0.08)'
                                }
                              }
                            },
                            plugins: {
                              legend: {
                                display: false
                              },
                              tooltip: {
                                backgroundColor: 'rgba(255, 255, 255, 0.97)',
                                titleColor: '#334155',
                                bodyColor: '#334155',
                                bodyFont: {
                                  family: "'Poppins', sans-serif",
                                  size: 14
                                },
                                titleFont: {
                                  family: "'Poppins', sans-serif",
                                  size: 16,
                                  weight: 'bold'
                                },
                                padding: 15,
                                cornerRadius: 10,
                                borderColor: 'rgba(63, 81, 181, 0.2)',
                                borderWidth: 1,
                                callbacks: {
                                  title: function(tooltipItems) {
                                    return tooltipItems[0].label;
                                  },
                                  label: function(context) {
                                    // Returnerer en tom string for at fjerne værdi-visningen
                                    return '';
                                  }
                                }
                              }
                            },
                            responsive: true,
                            maintainAspectRatio: false,
                            elements: {
                              line: {
                                tension: 0.2,
                                borderWidth: 3
                              },
                              point: {
                                radius: 6,
                                hoverRadius: 10
                              }
                            },
                            animation: {
                              duration: 1500,
                              easing: 'easeOutQuart'
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Typography variant="h6" sx={{ mb: 2 }}>{t('dashboard.improvementPoints')}</Typography>
                      <Paper sx={{ p: 2, borderRadius: 2 }}>
                        {getImprovementPoints(selectedAgent.id).map((point, index) => (
                          <Box key={point.id} sx={{ mb: index < getImprovementPoints(selectedAgent.id).length - 1 ? 2 : 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Box 
                                sx={{ 
                                  width: 10, 
                                  height: 10, 
                                  borderRadius: '50%', 
                                  bgcolor: point.priority === 'high' ? 'error.main' : 
                                           point.priority === 'medium' ? 'warning.main' : 'info.main',
                                  mr: 1.5
                                }} 
                              />
                              <Typography variant="subtitle2">{point.description}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
                              {point.priority === 'high' ? t('dashboard.highPriority') : 
                               point.priority === 'medium' ? t('dashboard.mediumPriority') : t('dashboard.lowPriority')}
                            </Typography>
                            {index < getImprovementPoints(selectedAgent.id).length - 1 && <Divider sx={{ mt: 2 }} />}
                          </Box>
                        ))}
                      </Paper>
                    </Grid>
                  </Grid>
                </AgentDetailCard>
              </Box>
            )}
          </Box>
        );
        
      case "Alerts":
        return (
          <>
            <Typography variant="h4" gutterBottom>
              {t('dashboard.notifications')}
            </Typography>
            <Card sx={{ borderRadius: '12px' }}>
              <CardContent>
                <Typography variant="body1">
                  {t('dashboard.notificationsComingSoon')}
                </Typography>
              </CardContent>
            </Card>
          </>
        );
        
      default:
        return <Typography>{t('dashboard.selectCategory')}</Typography>;
    }
  };
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  
  return (
    <AppContainer>
      <Sidebar>
        <MenuItemContainer 
          onClick={() => handleNavigate("Oversigt")}
          sx={{ 
            bgcolor: activeSection === "Oversigt" ? 'primary.main' : 'transparent',
            color: activeSection === "Oversigt" ? 'white' : 'inherit'
          }}
        >
          <DashboardIcon />
          <MenuItemText>{t('dashboard.overview')}</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer 
          onClick={() => handleNavigate("Medarbejdere")}
          sx={{ 
            bgcolor: activeSection === "Medarbejdere" ? 'primary.main' : 'transparent',
            color: activeSection === "Medarbejdere" ? 'white' : 'inherit'
          }}
        >
          <PeopleIcon />
          <MenuItemText>{t('dashboard.agents')}</MenuItemText>
        </MenuItemContainer>
        
        <MenuItemContainer 
          onClick={() => handleNavigate("Alerts")}
          sx={{ 
            bgcolor: activeSection === "Alerts" ? 'primary.main' : 'transparent',
            color: activeSection === "Alerts" ? 'white' : 'inherit'
          }}
        >
          <NotificationsIcon />
          <MenuItemText>{t('dashboard.alerts')}</MenuItemText>
        </MenuItemContainer>
      </Sidebar>
      
      <MainContent>
        <Header>
          <Typography variant="h5">{t('dashboard.managementDashboard')}</Typography>
        </Header>
        
        <ContentBox>
          {loading && activeSection === "Oversigt" ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            renderContent()
          )}
        </ContentBox>
      </MainContent>
    </AppContainer>
  );
};

export default ManagementDashboard; 